import React, { useEffect, useState } from "react"
import { Loader2, Plus, Trash, UserRoundPlus, ChevronRight, RotateCw } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { useAdminStore } from "@/stores/admin-store"
import { CreateOrgDialog } from "@/components/admin/CreateOrgDialog"
import { InviteUserDialog } from "@/components/admin/InviteUserDialog"
import { OrgHealthPanel } from "@/components/admin/OrgHealthPanel"
import { cn } from "@/lib/utils"

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  })
}

function formatRelativeDate(dateStr: string | null): string {
  if (!dateStr) return "Never"
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return "Today"
  if (diffDays === 1) return "Yesterday"
  if (diffDays < 7) return `${diffDays}d ago`
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`
  return formatDate(dateStr)
}

export function AdminOrganizationsPage() {
  const {
    organizations,
    isOrganizationsLoading,
    loadOrganizations,
    createOrganization,
    inviteUserToOrg,
    loadOrgUsers,
    orgUsers,
    orgUsersLoading,
    loadOrgInvitations,
    orgInvitations,
    orgInvitationsLoading,
    resendInvitation,
    toggleOrgActive,
    softDeleteOrg,
  } = useAdminStore()
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [invitingOrg, setInvitingOrg] = useState<{ id: string; name: string } | null>(null)
  const [expandedOrgId, setExpandedOrgId] = useState<string | null>(null)
  const [togglingId, setTogglingId] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [confirmDeactivate, setConfirmDeactivate] = useState<{ id: string; name: string } | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<{ id: string; name: string } | null>(null)
  const [resendingId, setResendingId] = useState<string | null>(null)

  useEffect(() => {
    void loadOrganizations()
  }, [loadOrganizations])

  const handleToggleExpand = (orgId: string) => {
    if (expandedOrgId === orgId) {
      setExpandedOrgId(null)
      return
    }
    setExpandedOrgId(orgId)
    if (!orgUsers[orgId]) {
      void loadOrgUsers(orgId)
    }
    if (!orgInvitations[orgId]) {
      void loadOrgInvitations(orgId)
    }
  }

  const handleResend = async (orgId: string, invitationId: string) => {
    setResendingId(invitationId)
    try {
      await resendInvitation(orgId, invitationId)
    } finally {
      setResendingId(null)
    }
  }

  const handleCreate = async (organizationName: string, adminEmail: string, role: string) => {
    const result = await createOrganization(organizationName, adminEmail, role)
    return { inviteUrl: result.inviteUrl }
  }

  const handleToggleActive = async (id: string, currentlyActive: boolean) => {
    // Activating doesn't need confirmation
    if (!currentlyActive) {
      setTogglingId(id)
      try {
        await toggleOrgActive(id, true)
      } finally {
        setTogglingId(null)
      }
      return
    }
    // Deactivating needs confirmation
    const org = organizations.find((o) => o.id === id)
    setConfirmDeactivate({ id, name: org?.name ?? "this organisation" })
  }

  const handleConfirmDeactivate = async () => {
    if (!confirmDeactivate) return
    setTogglingId(confirmDeactivate.id)
    setConfirmDeactivate(null)
    try {
      await toggleOrgActive(confirmDeactivate.id, false)
    } finally {
      setTogglingId(null)
    }
  }

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return
    setDeletingId(confirmDelete.id)
    setConfirmDelete(null)
    try {
      await softDeleteOrg(confirmDelete.id)
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <div className="flex flex-col h-full">
      {/* Content */}
      <div className="flex-1 overflow-auto px-6 py-4">
        <div className="flex items-center justify-end mb-4">
          <Button size="sm" onClick={() => setShowCreateDialog(true)}>
            <Plus className="h-4 w-4 mr-1" />
            Create Organisation
          </Button>
        </div>
        {isOrganizationsLoading ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : organizations.length === 0 ? (
          <p className="text-sm text-muted-foreground py-8 text-center">
            No organisations yet.
          </p>
        ) : (
          <div className="border rounded-lg overflow-hidden bg-card">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">Organisation</th>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">Users</th>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">Bids</th>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">Docs</th>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">Last Active</th>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">Status</th>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">Created</th>
                  <th className="text-right px-3 py-2 font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {organizations.map((org) => {
                  const isExpanded = expandedOrgId === org.id
                  const users = orgUsers[org.id]
                  const isLoadingUsers = orgUsersLoading[org.id]

                  return (
                    <React.Fragment key={org.id}>
                      <tr
                        className={cn("hover:bg-muted/30 cursor-pointer", isExpanded && "bg-muted/20")}
                        onClick={() => handleToggleExpand(org.id)}
                      >
                        <td className="px-3 py-2 font-medium">
                          <div className="flex items-center gap-1.5">
                            <ChevronRight className={cn("h-3.5 w-3.5 text-muted-foreground transition-transform", isExpanded && "rotate-90")} />
                            {org.name}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">{org.userCount}</td>
                        <td className="px-3 py-2 text-muted-foreground">{org.bidCount}</td>
                        <td className="px-3 py-2 text-muted-foreground">{org.documentCount}</td>
                        <td className="px-3 py-2 text-muted-foreground">{formatRelativeDate(org.lastActiveAt)}</td>
                        <td className="px-3 py-2">
                          <Badge variant={org.isActive ? "success" : "destructive"}>
                            {org.isActive ? "Active" : "Inactive"}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">{formatDate(org.createdAt)}</td>
                        <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              title="Invite user"
                              onClick={() => setInvitingOrg({ id: org.id, name: org.name })}
                            >
                              <UserRoundPlus className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={togglingId === org.id}
                              className={
                                org.isActive
                                  ? "text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                                  : ""
                              }
                              onClick={() => void handleToggleActive(org.id, org.isActive)}
                            >
                              {togglingId === org.id ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : org.isActive ? (
                                "Deactivate"
                              ) : (
                                "Activate"
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              disabled={deletingId === org.id}
                              className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                              onClick={() => setConfirmDelete({ id: org.id, name: org.name })}
                            >
                              {deletingId === org.id ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <Trash className="h-3.5 w-3.5" />
                              )}
                            </Button>
                          </div>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={8} className="bg-muted/10 px-3 py-3">
                            <div className="pl-5">
                              <OrgHealthPanel organizationId={org.id} />
                            </div>

                            {isLoadingUsers ? (
                              <div className="flex items-center gap-2 py-2 pl-5">
                                <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                                <span className="text-meta">Loading users...</span>
                              </div>
                            ) : !users || users.length === 0 ? (
                              <p className="text-meta py-2 pl-5">No users in this organisation.</p>
                            ) : (
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="text-muted-foreground">
                                    <th className="text-left px-2 py-1 pl-5 font-medium">Email</th>
                                    <th className="text-left px-2 py-1 font-medium">Name</th>
                                    <th className="text-left px-2 py-1 font-medium">Role</th>
                                    <th className="text-left px-2 py-1 font-medium">Joined</th>
                                    <th className="text-left px-2 py-1 font-medium">Last Active</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {users.map((user) => (
                                    <tr key={user.id} className="border-t border-border/50">
                                      <td className="px-2 py-1.5 pl-5">{user.email}</td>
                                      <td className="px-2 py-1.5 text-muted-foreground">{user.fullName ?? "—"}</td>
                                      <td className="px-2 py-1.5">
                                        <Badge variant={user.role === "admin" ? "default" : "secondary"} className="text-[10px] px-1.5 py-0">
                                          {user.role}
                                        </Badge>
                                      </td>
                                      <td className="px-2 py-1.5 text-muted-foreground">{formatDate(user.createdAt)}</td>
                                      <td className="px-2 py-1.5 text-muted-foreground">{formatRelativeDate(user.lastActiveAt)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}

                            {/* Pending Invitations */}
                            {orgInvitationsLoading[org.id] ? (
                              <div className="flex items-center gap-2 py-2 pl-5 mt-3">
                                <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                                <span className="text-meta">Loading invitations...</span>
                              </div>
                            ) : (orgInvitations[org.id] ?? []).length > 0 && (
                              <div className="mt-3">
                                <p className="text-xs font-medium text-muted-foreground pl-5 mb-1">Pending Invitations</p>
                                <table className="w-full text-xs">
                                  <thead>
                                    <tr className="text-muted-foreground">
                                      <th className="text-left px-2 py-1 pl-5 font-medium">Email</th>
                                      <th className="text-left px-2 py-1 font-medium">Role</th>
                                      <th className="text-left px-2 py-1 font-medium">Sent</th>
                                      <th className="text-left px-2 py-1 font-medium">Expires</th>
                                      <th className="text-right px-2 py-1 font-medium">Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {(orgInvitations[org.id] ?? []).map((inv) => (
                                      <tr key={inv.id} className="border-t border-border/50">
                                        <td className="px-2 py-1.5 pl-5">{inv.email}</td>
                                        <td className="px-2 py-1.5">
                                          <Badge variant={inv.role === "admin" ? "default" : "secondary"} className="text-[10px] px-1.5 py-0">
                                            {inv.role}
                                          </Badge>
                                        </td>
                                        <td className="px-2 py-1.5 text-muted-foreground">{formatRelativeDate(inv.createdAt)}</td>
                                        <td className="px-2 py-1.5 text-muted-foreground">{formatDate(inv.expiresAt)}</td>
                                        <td className="px-2 py-1.5 text-right">
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="text-xs h-6 px-2"
                                            disabled={resendingId === inv.id}
                                            onClick={() => void handleResend(org.id, inv.id)}
                                          >
                                            {resendingId === inv.id ? (
                                              <Loader2 className="h-3 w-3 animate-spin" />
                                            ) : (
                                              <>
                                                <RotateCw className="h-3 w-3 mr-1" />
                                                Resend
                                              </>
                                            )}
                                          </Button>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Dialog */}
      <CreateOrgDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onSubmit={handleCreate}
      />

      {/* Invite User Dialog */}
      <InviteUserDialog
        open={!!invitingOrg}
        onOpenChange={(open) => { if (!open) setInvitingOrg(null) }}
        organizationName={invitingOrg?.name ?? ""}
        onSubmit={async (email, role) => {
          if (!invitingOrg) throw new Error("No organisation selected")
          return inviteUserToOrg(invitingOrg.id, email, role)
        }}
      />

      {/* Deactivate Confirmation */}
      <AlertDialog open={!!confirmDeactivate} onOpenChange={(open) => { if (!open) setConfirmDeactivate(null) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate Organisation</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to deactivate <strong>{confirmDeactivate?.name}</strong>? Users in this organisation will lose access until it is reactivated.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); void handleConfirmDeactivate() }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(open) => { if (!open) setConfirmDelete(null) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Organisation</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{confirmDelete?.name}</strong>? The organisation will be removed from all views. Data will be retained in the database but will no longer be accessible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); void handleConfirmDelete() }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
